import React, { useEffect, useMemo, useState } from "react";
import { Check, CircleAlert, Info, Loader, TriangleAlert } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import SectionLabel from "../common/SectionLabel";
import { MIN_LOCK_DAYS, XP_DISCLAIMER } from "../../config/supernovaXp";
import { ConfirmRiskBlock, StakeRiskBanner } from "./SupernovaDisclosure";
import SupernovaOrb from "./SupernovaOrb";
import { buildExitQuote } from "./supernovaExitQuote";
import {
  fmtCount,
  fmtDate,
  fmtMultiplier,
  fmtPenaltyPct,
  fmtXpExact,
  shortAddr,
} from "./supernovaFormat";
import useSupernovaUnstake, { TX, isTxBusy } from "./useSupernovaUnstake";

/**
 * The unstake modal — two very different conversations behind one button.
 *
 *   MATURED     the lock ran its course. There is nothing to forfeit and nothing
 *               to explain away: keep every point at the rate that was
 *               contracted, sign, and the NFTs come home.
 *   EARLY EXIT  the member is about to give something up. Every line of what
 *               they give up is on screen BEFORE the signature — accrued at the
 *               rate they chose (struck through, because they don't get it), the
 *               re-rate down to the tier they actually served, the decay penalty
 *               with the date it reaches zero, and the unearned remainder that
 *               was never accrued in the first place.
 *
 * THE SAME THREE REFUSALS AS THE STAKE MODAL:
 *
 *  1. It does not compute. Every figure comes from supernovaExitQuote →
 *     config/supernovaXp. This file renders numbers, it never derives them.
 *  2. It does not fake a success. No signing path in this build ⇒ a plainly
 *     labelled dead end, never a spinner that resolves into an unstake that
 *     never happened.
 *  3. It does not sign. The member signs in their own wallet. ARCrypto holds no
 *     key and has no path that could.
 *
 * THE QUOTE IS FROZEN AT OPEN. The penalty decays continuously, so a figure that
 * moved while the member was reading it would be a different number by the time
 * they signed. It is taken once, stamped, and the caption says so.
 *
 * A QUOTE IS NOT A SETTLEMENT. What is actually released is decided by the
 * contract. The copy says that in as many words, and nothing here may promise
 * otherwise.
 *
 * COMPLIANCE: XP is an in-app points balance. The one percentage on this screen
 * is the early-exit penalty — a share of points forfeited, never a rate of
 * return — and the risk disclosure is on screen the whole time the Unstake
 * button is. No currency figure, no return, no yield, no income, ever.
 *
 * @param {Object} props
 * @param {boolean} [props.open]
 * @param {Object} props.lot            a StakeLotRecord from getStaked
 * @param {string|null} props.wallet    connected address (they sign, we don't)
 * @param {(info: {hash: string|null}) => void} props.onClose
 * @param {(result: Object) => void} [props.onUnstaked] fired once, on confirmation
 * @param {Object} [props.service]      injectable supernovaChain (tests)
 * @param {number} [props.nowMs]        injectable clock (tests)
 */
const SupernovaUnstakeModal = ({
  open = true,
  lot,
  wallet = null,
  onClose,
  onUnstaked,
  service,
  nowMs,
}) => {
  /* Frozen on open: a decaying penalty that ticked while the member read it
     would quote one number and sign another. */
  const [quotedAtMs] = useState(() => nowMs ?? Date.now());
  const quote = useMemo(() => buildExitQuote(lot, quotedAtMs), [lot, quotedAtMs]);

  const { unstakeTx, submit, resetTx } = useSupernovaUnstake(wallet, {
    enabled: open,
    service,
  });

  const inTxPhase = unstakeTx.state !== TX.IDLE;
  const busy = isTxBusy(unstakeTx.state);
  const { row } = quote;

  /* Confirmation is the only thing that tells the tab to refresh and toast. It
     fires once — a re-render can't replay it, because CONFIRMED is terminal. */
  useEffect(() => {
    if (unstakeTx.state !== TX.CONFIRMED) return;
    onUnstaked?.({
      lotId: row.lotId,
      tokenId: row.tokenId,
      tokenName: row.tokenName,
      qty: row.qty,
      matured: quote.matured,
      /* The model's figure for this exit, passed through for the toast. It is
         what the quote said, NOT a claim about what the contract released. */
      keptXp: quote.valid ? quote.keptXp : null,
      hash: unstakeTx.hash,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unstakeTx.state]);

  const close = () => onClose?.({ hash: unstakeTx.hash || null });

  /* While a wallet prompt is open or a transaction is in flight, Esc and the
     overlay do nothing — closing mid-signature would drop the state that tells
     the member (and the tab) what actually happened. */
  const dismiss = busy ? () => {} : close;

  const title = quote.matured ? "Unstake — matured lock" : "Unstake early";

  return (
    <Modal open={open} onClose={dismiss} title={title}>
      <div className="text-left" data-testid="supernova-unstake-modal">
        <p className="text-[12.5px] text-arc-ink-3 -mt-1 mb-4 tnum">
          <LotSummary quote={quote} />
        </p>

        {inTxPhase ? (
          <TxPhase tx={unstakeTx} quote={quote} />
        ) : (
          <>
            {quote.valid ? (
              quote.matured ? (
                <MaturedQuote quote={quote} />
              ) : (
                <EarlyExitQuote quote={quote} />
              )
            ) : (
              <UnreadableLot />
            )}
            {/* Only alongside a real quote: describing what signing would do,
                under a panel that says we can't quote it, would be an offer we
                aren't making. */}
            {quote.valid && <ExitNote quote={quote} />}
          </>
        )}

        <div className="mt-4">
          <StakeRiskBanner />
        </div>

        <Actions
          inTxPhase={inTxPhase}
          tx={unstakeTx}
          quote={quote}
          wallet={wallet}
          onSubmit={() =>
            /* Which of the contract's two exits this is, decided here because
               this is the screen that told the member which one they're taking.
               The two are indistinguishable on-chain afterwards, so it is never
               inferred further down. */
            submit({ lotId: row.lotId, emergency: !quote.matured })
          }
          onRetry={resetTx}
          onClose={close}
        />
      </div>
    </Modal>
  );
};

/* ── The lot, in one line ───────────────────────────────────────────────── */

const LotSummary = ({ quote }) => {
  const { row } = quote;
  if (!row.readable) {
    return <>We couldn&apos;t read the details of this stake.</>;
  }
  return (
    <>
      {fmtCount(row.qty)}× {row.tokenName} · {row.lockName} lock at{" "}
      {fmtMultiplier(row.multiplier)} · {fmtXpExact(row.earnedXp)} XP accrued of{" "}
      {fmtXpExact(row.xpAtMaturity)} XP at maturity
    </>
  );
};

/* ── Matured: the clean exit ────────────────────────────────────────────── */

/**
 * A matured lot has no re-rate and no penalty, so this screen deliberately has
 * no arithmetic on it — spelling out losses that don't apply would make a clean
 * outcome look like a negotiation.
 */
const MaturedQuote = ({ quote }) => (
  <div
    className="rounded-[14px] bg-arc-pos-soft ring-1 ring-arc-line px-4 py-3.5"
    data-testid="supernova-exit-quote"
    data-exit-kind="matured"
  >
    <SectionLabel>Matured — clean exit</SectionLabel>
    <p className="text-hero-num text-arc-pos tnum mt-0.5">
      Keep all {fmtXpExact(quote.keptXp)} XP
    </p>
    <p className="text-[12.5px] text-arc-ink-2 mt-1.5 leading-[1.6]">
      Your lock reached its unlock date on {fmtDate(quote.row.unlockAtMs)}, so the
      full term accrued at the {fmtMultiplier(quote.chosenMultiplier)} rate you
      contracted. Nothing is re-rated and no early-exit penalty applies. Accrual
      on this lot stops when it closes.
    </p>
    <p className="text-caption text-arc-ink-3 mt-2">{XP_DISCLAIMER}</p>
  </div>
);

/* ── Early exit: the three stacked losses, line by line ─────────────────── */

/**
 * The order matters and is the model's own: what the chosen rate accrued, what
 * the served tier re-rates it to, what the decay penalty takes off that, and —
 * last, separately — the unearned remainder, which is not a deduction at all.
 * Putting the forfeited-unearned line inside the subtraction would double-count
 * a loss that never existed.
 */
const EarlyExitQuote = ({ quote }) => (
  <div
    className="rounded-[14px] bg-arc-paper-2 ring-1 ring-arc-line px-4 py-3.5"
    data-testid="supernova-exit-quote"
    data-exit-kind="early"
  >
    <SectionLabel>
      Early exit — re-rated to the tier you served, minus the penalty
    </SectionLabel>
    <p className="text-hero-num text-arc-ink tnum mt-0.5">
      Keep {fmtXpExact(quote.keptXp)} XP
    </p>

    <dl className="mt-3 space-y-2 text-[12.5px] leading-[1.5] tnum">
      <Line label={`Accrued at your ${fmtMultiplier(quote.chosenMultiplier)} lock rate`}>
        {/* Struck through because this is precisely what an early exit does NOT
            pay — showing it plain would read as the starting point. */}
        <span className="text-arc-ink-4 line-through" data-testid="supernova-exit-struck">
          {fmtXpExact(quote.earnedAtChosenXp)} XP
        </span>
      </Line>

      <Line label={<ReratedLabel quote={quote} />}>
        <b className="text-arc-ink-2">{fmtXpExact(quote.reratedXp)} XP</b>
      </Line>

      <Line
        label={
          <>
            Early-exit penalty (
            <b className="text-arc-neg">{fmtPenaltyPct(quote.penaltyPct)} now</b> —
            shrinks to 0% by {fmtDate(quote.penaltyEndsAtMs)})
          </>
        }
      >
        <b className="text-arc-neg">−{fmtXpExact(quote.penaltyXp)} XP</b>
      </Line>

      <Line label="Unearned XP forfeited (never accrued, so never yours to lose)">
        <span className="text-arc-ink-4">{fmtXpExact(quote.unearnedForfeitedXp)} XP</span>
      </Line>
    </dl>

    <p className="text-caption text-arc-ink-3 mt-2.5">
      Quoted just now, for this moment. The penalty decays every day and reaches
      zero on {fmtDate(quote.penaltyEndsAtMs)} — waiting always keeps more points
      than leaving today. {XP_DISCLAIMER}
    </p>
  </div>
);

/** Names the tier the model re-rated to, or says plainly that none was served. */
const ReratedLabel = ({ quote }) =>
  quote.servedTier ? (
    <>
      Re-rated to the{" "}
      <b className="text-arc-ink-2">{quote.servedTier.name} tier</b> you actually
      completed ({fmtMultiplier(quote.servedTier.multiplier)})
    </>
  ) : (
    <>
      No lock tier completed — under the {MIN_LOCK_DAYS}-day minimum, so this
      re-rates to the {fmtMultiplier(quote.servedTierMultiplier)} base rate
    </>
  );

const Line = ({ label, children }) => (
  <div className="flex justify-between items-baseline gap-4">
    <dt className="text-arc-ink-3 flex-1">{label}</dt>
    <dd className="flex-none whitespace-nowrap">{children}</dd>
  </div>
);

/* ── A lot we couldn't rate ─────────────────────────────────────────────── */

const UnreadableLot = () => (
  <div
    className="flex gap-3 rounded-[13px] bg-arc-paper-2 ring-1 ring-arc-line-2 p-4"
    data-testid="supernova-exit-unreadable"
  >
    <CircleAlert size={18} className="flex-shrink-0 mt-0.5 text-arc-ink-3" aria-hidden="true" />
    <div className="text-[13.5px] leading-[1.55] text-arc-ink-2">
      <p className="font-semibold text-arc-ink mb-1">
        We can&apos;t quote an exit for this stake
      </p>
      <p>
        Its details came back in a form we can&apos;t rate, so any figure we put
        here would be invented. Nothing about the stake has changed — it is still
        held by the staking contract, and you can always unstake it directly from
        your own wallet.
      </p>
    </div>
  </div>
);

/* ── What signing actually does ─────────────────────────────────────────── */

const ExitNote = ({ quote }) => (
  <p className="text-[12.5px] text-arc-ink-3 leading-[1.6] mt-3">
    {quote.matured
      ? "Your wallet signs the unstake. The NFTs return to your wallet and this lot stops accruing. The strategy slots it backed close with it."
      : "Your wallet signs the early unstake. The NFTs return to your wallet immediately, the lot closes, and the strategy slots it backed close with it. This cannot be reversed — the lock cannot be reopened at the rate you had."}{" "}
    The figures above are this app&apos;s reading of the model at the moment you
    opened this window; the staking contract decides what is actually released.
  </p>
);

/* ── Transaction states ─────────────────────────────────────────────────── */

/**
 * One panel per outcome, mirroring the stake modal exactly — same states, same
 * language, same refusal to promote "submitted" to "confirmed". The UNAVAILABLE
 * branch is the important one: this build has no signing path, and it says so
 * instead of pretending the unstake went through.
 */
const TxPhase = ({ tx, quote }) => {
  const { row } = quote;
  return (
    <div data-testid="supernova-unstake-tx" data-tx-state={tx.state} className="space-y-3">
      <div className="rounded-[13px] bg-arc-paper-2 ring-1 ring-arc-line px-4 py-3">
        <SectionLabel>What you asked for</SectionLabel>
        <p className="text-[13.5px] text-arc-ink mt-0.5 tnum flex items-center gap-2">
          {/* Decorative: the sentence it sits in names the token. */}
          <SupernovaOrb tokenId={row.tokenId} name={null} size={18} />
          {row.readable
            ? `Unstake ${fmtCount(row.qty)} × ${row.tokenName} · ${
                quote.matured ? "matured lock" : "early exit"
              }`
            : "Unstake this lot"}
        </p>
        {/* Same component as the stake confirm, for the same reason: it is the
            last thing read before a signature, and the two must not be able to
            say it differently. */}
        <ConfirmRiskBlock />
      </div>

      {tx.state === TX.PENDING && (
        <StatePanel
          icon={Loader}
          iconClass="text-arc-accent animate-spin"
          title="Unstake: waiting for your signature"
          body={tx.message}
        />
      )}

      {tx.state === TX.SIGNED && (
        <StatePanel
          icon={Loader}
          iconClass="text-arc-accent animate-spin"
          title="Unstake: signed, awaiting confirmation"
          body={tx.message}
          hash={tx.hash}
        />
      )}

      {tx.state === TX.CONFIRMED && (
        <StatePanel
          icon={Check}
          iconClass="text-arc-pos"
          tone="pos"
          title="Unstake confirmed on Arbitrum"
          body={
            quote.matured
              ? "Your NFTs are on their way back to your wallet and this lot has closed. It no longer accrues."
              : "Your NFTs are on their way back to your wallet and this lot has closed early. The contract settles what is released; the quote above was the model's reading of it."
          }
          hash={tx.hash}
        />
      )}

      {tx.state === TX.REJECTED && (
        <StatePanel
          icon={Info}
          iconClass="text-arc-ink-3"
          title="You declined the signature"
          body={`${tx.message} Your stake is untouched and still accruing.`}
        />
      )}

      {tx.state === TX.UNAVAILABLE && (
        <div
          className="flex gap-3 rounded-[13px] bg-arc-paper-2 ring-1 ring-arc-line-2 p-4"
          data-testid="supernova-signing-unavailable"
        >
          <CircleAlert size={18} className="flex-shrink-0 mt-0.5 text-arc-ink-3" aria-hidden="true" />
          <div className="text-[13.5px] leading-[1.55] text-arc-ink-2">
            <p className="font-semibold text-arc-ink mb-1">
              Signing is unavailable in this build
            </p>
            <p>
              {/* TODO(backend, supervised): the staking contract's write path
                  ships under human supervision (supernovaChain.unstake). Until
                  it does this is the end of the flow — and it must stay an
                  honest dead end rather than a fake unstake. */}
              This build can quote an exit but cannot submit one: {tx.message}{" "}
              <b>Nothing was signed and nothing changed on-chain.</b> Your NFTs
              are still staked, your lock is unchanged, and XP keeps accruing
              exactly as before.
            </p>
            <p className="text-caption text-arc-ink-3 mt-2">
              To unstake today, use the contract directly from your own wallet —
              this app never signs on your behalf either way.
            </p>
          </div>
        </div>
      )}

      {tx.state === TX.ERROR && (
        <StatePanel
          icon={TriangleAlert}
          iconClass="text-arc-neg"
          tone="neg"
          title="Unstake didn't go through"
          body={tx.message}
        />
      )}
    </div>
  );
};

const TONES = {
  neutral: "bg-arc-paper-2 ring-arc-line",
  pos: "bg-arc-pos-soft ring-arc-line",
  neg: "bg-arc-neg-soft ring-arc-line",
};

const StatePanel = ({ icon: Icon, iconClass, tone = "neutral", title, body, hash }) => (
  <div className={`flex gap-3 rounded-[13px] ring-1 p-4 ${TONES[tone]}`}>
    <Icon size={18} className={`flex-shrink-0 mt-0.5 ${iconClass}`} aria-hidden="true" />
    <div className="text-[13.5px] leading-[1.55] text-arc-ink-2">
      <p className="font-semibold text-arc-ink mb-0.5">{title}</p>
      <p>{body}</p>
      {hash && (
        <p className="text-caption text-arc-ink-4 mt-1.5 tnum break-all">
          Transaction {shortAddr(hash)}
        </p>
      )}
    </div>
  </div>
);

/* ── Footer actions ─────────────────────────────────────────────────────── */

/**
 * "Keep staked" is the secondary action on purpose: the safe choice is the one
 * that changes nothing, and it stays available at every step before a signature.
 */
const Actions = ({ inTxPhase, tx, quote, wallet, onSubmit, onRetry, onClose }) => {
  if (inTxPhase) {
    const busy = isTxBusy(tx.state);
    return (
      <Row>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          {tx.state === TX.CONFIRMED ? "Done" : "Close"}
        </Button>
        {(tx.state === TX.REJECTED || tx.state === TX.ERROR) && (
          <Button onClick={onRetry}>Try again</Button>
        )}
        {busy && (
          <span className="text-caption text-arc-ink-3">
            Your wallet is handling this — it can&apos;t be cancelled from here.
          </span>
        )}
      </Row>
    );
  }

  return (
    <Row>
      <Button variant="secondary" onClick={onClose}>
        Keep staked
      </Button>
      <Button
        onClick={onSubmit}
        /* No wallet ⇒ nothing can sign, so the button says so rather than
           silently doing nothing when pressed. */
        disabled={!quote.valid || !wallet}
        title={
          !quote.valid
            ? "We can't read this stake, so we won't ask you to sign anything against it."
            : !wallet
            ? "Connect the wallet that holds this stake — it is the only thing that can sign the unstake."
            : undefined
        }
      >
        Unstake &amp; sign
      </Button>
    </Row>
  );
};

const Row = ({ children }) => (
  <div className="flex items-center justify-end gap-2.5 flex-wrap mt-4">{children}</div>
);

export default SupernovaUnstakeModal;
